import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ic } from '../icons';
import { uploadUrl } from '../content';
import { PDF_BASE, loadPdfLibrary, readPdfPageText } from './pdfViewer';
import './DocumentViewer.css';

export const documentUrl = (file) => {
  if (!file) return '';
  // Admin files are upload paths. Allow only HTTP(S) for migrated external files.
  if (/^https?:\/\//i.test(file)) return file;
  if (/^[a-z][a-z\d+.-]*:/i.test(file) || file.startsWith('//')) return '';
  return file.startsWith('/assets/') ? file : uploadUrl(file);
};
export const documentDownloadUrl = (file) => {
  const url = documentUrl(file);
  return url ? `${url}${url.includes('?') ? '&' : '?'}download=1` : '';
};

export default function DocumentViewer({ doc, lang = 'ru', onClose }) {
  const kz = lang === 'kz';
  const title = doc[`name_${lang}`] || doc.name_ru || (kz ? 'Турнир регламенті' : 'Регламент турнира');
  const url = documentUrl(doc.file);
  const isPdf = /\.pdf(?:[?#]|$)/i.test(url);
  const dialog = useRef(null);
  const closeButton = useRef(null);
  const scroller = useRef(null);
  const pageHost = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(300);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [text, setText] = useState('');
  const [textMode, setTextMode] = useState(false);
  const [textBusy, setTextBusy] = useState(false);
  const [textError, setTextError] = useState(false);
  const contentBusy = textMode && pdf ? textBusy : busy;
  const contentError = error && (!textMode || !pdf);

  useEffect(() => {
    const trigger = document.activeElement;
    const top = window.scrollY;
    const body = document.body;
    const old = { position: body.style.position, top: body.style.top, width: body.style.width, overflow: body.style.overflow };
    Object.assign(body.style, { position: 'fixed', top: `-${top}px`, width: '100%', overflow: 'hidden' });
    const root = document.getElementById('root');
    const wasInert = root?.inert;
    if (root) root.inert = true;
    closeButton.current?.focus();
    const key = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); }
      if (event.key !== 'Tab') return;
      const items = [...dialog.current.querySelectorAll('button:not(:disabled),a[href],select:not(:disabled),[tabindex="0"]')].filter((x) => x.getClientRects().length);
      const first = items[0]; const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    window.addEventListener('keydown', key);
    return () => {
      Object.assign(body.style, old);
      if (root) root.inert = wasInert;
      window.removeEventListener('keydown', key);
      window.scrollTo({ top, behavior: 'instant' });
      trigger?.focus?.({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const node = scroller.current;
    const measure = () => setWidth(Math.max(200, node.clientWidth - 32));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let live = true; let task;
    setPdf(null); setPage(1); setError(false); setBusy(true);
    if (isPdf) {
      loadPdfLibrary().then((lib) => {
        if (!live) return;
        task = lib.getDocument({ url, cMapUrl: `${PDF_BASE}/cmaps/`, cMapPacked: true, standardFontDataUrl: `${PDF_BASE}/standard_fonts/`, wasmUrl: `${PDF_BASE}/wasm/`, isEvalSupported: false });
        return task.promise.then((value) => { if (live) setPdf(value); });
      }).catch((err) => {
        if (live) { console.warn('[DocumentViewer] PDF load failed', err); setError(true); setBusy(false); }
      });
    } else if (!url) { setError(true); setBusy(false); }
    return () => { live = false; task?.destroy().catch(() => {}); };
  }, [url, isPdf, attempt]);

  useEffect(() => {
    if (!pdf) return undefined;
    let live = true; let rendering;
    const host = pageHost.current;
    // A new canvas per render avoids races when switching pages/zoom rapidly.
    const canvas = document.createElement('canvas');
    canvas.setAttribute('aria-hidden', 'true');
    setBusy(true); setError(false);
    host.replaceChildren();
    scroller.current.scrollTo({ top: 0, left: 0 });
    pdf.getPage(page).then(async (value) => {
      if (!live) return;
      const base = value.getViewport({ scale: 1 });
      const viewport = value.getViewport({ scale: Math.min(width, 900) * zoom / base.width });
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * ratio); canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = `${viewport.width}px`; canvas.style.height = `${viewport.height}px`;
      host.appendChild(canvas);
      rendering = value.render({ canvas, canvasContext: canvas.getContext('2d'), viewport, transform: [ratio, 0, 0, ratio, 0, 0] });
      // Text extraction must never hide a successfully rendered PDF page.
      await rendering.promise;
      if (!live) return;
      setBusy(false);
    }).catch((err) => {
      if (live && err.name !== 'RenderingCancelledException') {
        console.warn('[DocumentViewer] PDF page render failed', err);
        setError(true); setBusy(false);
      }
    });
    return () => {
      live = false; rendering?.cancel(); canvas.remove();
      canvas.width = 0; canvas.height = 0;
    };
  }, [pdf, page, zoom, width]);

  useEffect(() => {
    setText(''); setTextError(false);
    if (!pdf) { setTextBusy(false); return undefined; }
    const controller = new AbortController();
    setTextBusy(true);
    pdf.getPage(page).then((value) => readPdfPageText(value, controller.signal)).then((value) => {
      if (!controller.signal.aborted) setText(value);
    }).catch((err) => {
      if (!controller.signal.aborted) {
        console.warn('[DocumentViewer] PDF text extraction failed', err);
        setTextError(true);
      }
    }).finally(() => { if (!controller.signal.aborted) setTextBusy(false); });
    return () => controller.abort();
  }, [pdf, page]);

  return createPortal(
    <div className="fed-document-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section ref={dialog} className="fed-document-dialog" role="dialog" aria-modal="true" aria-labelledby="fed-document-title">
        <header className="fed-document-header">
          <div><span className="fed-document-eyebrow">{isPdf ? 'PDF' : (kz ? 'Құжат' : 'Документ')} · {kz ? 'Сайтта оқу' : 'Просмотр на сайте'}</span><h2 id="fed-document-title">{title}</h2></div>
          <button ref={closeButton} type="button" onClick={onClose} aria-label={kz ? 'Жабу' : 'Закрыть'}><Ic name="x" size={23} /></button>
        </header>
        <div className="fed-document-toolbar">
          {isPdf && <div className="fed-document-pagination">
            <button type="button" disabled={!pdf || page <= 1} onClick={() => setPage(page - 1)} aria-label={kz ? 'Алдыңғы бет' : 'Предыдущая страница'}><span style={{ display: 'flex', transform: 'rotate(180deg)' }}><Ic name="chevR" size={20} /></span></button>
            <label><span className="sr-only">{kz ? 'Бет' : 'Страница'}</span><select value={page} disabled={!pdf} onChange={(e) => setPage(Number(e.target.value))}>{Array.from({ length: pdf?.numPages || 1 }, (_, i) => <option key={i} value={i + 1}>{i + 1} / {pdf?.numPages || '…'}</option>)}</select></label>
            <button type="button" disabled={!pdf || page >= pdf.numPages} onClick={() => setPage(page + 1)} aria-label={kz ? 'Келесі бет' : 'Следующая страница'}><Ic name="chevR" size={20} /></button>
          </div>}
          {isPdf && <div className="fed-document-view-controls">
            <label><span className="sr-only">Масштаб</span><select aria-label="Масштаб" value={zoom} disabled={textMode} onChange={(e) => setZoom(Number(e.target.value))}><option value={1}>{kz ? 'Ені бойынша' : 'По ширине'}</option><option value={1.5}>150%</option><option value={2}>200%</option></select></label>
            <button type="button" disabled={!pdf} onClick={() => setTextMode(!textMode)} aria-pressed={textMode}>{textMode ? 'PDF' : (kz ? 'Мәтін' : 'Текст')}</button>
          </div>}
          <a className="fed-document-download" href={documentDownloadUrl(doc.file)} download><Ic name="download" size={18} />{kz ? 'Жүктеу' : (isPdf ? 'Скачать PDF' : 'Скачать')}</a>
        </div>
        <div ref={scroller} className="fed-document-content" tabIndex={0} aria-label={kz ? 'Құжат мазмұны' : 'Содержимое документа'} aria-busy={contentBusy}>
          {contentBusy && <p className="fed-document-status" role="status">{kz ? 'Құжат жүктелуде…' : 'Загружаем документ…'}</p>}
          {contentError && <div className="fed-document-status" role="alert"><p>{kz ? 'Құжат ашылмады. Қайталап көріңіз немесе PDF-ті бөлек ашыңыз.' : 'Не удалось открыть документ. Повторите попытку или откройте PDF отдельно.'}</p><button type="button" onClick={() => setAttempt(attempt + 1)}>{kz ? 'Қайталау' : 'Повторить'}</button><a href={url} target="_blank" rel="noopener noreferrer">{kz ? 'Файлды ашу' : 'Открыть файл'}</a></div>}
          {isPdf ? <><div ref={pageHost} className="fed-document-page" hidden={textMode || error} /><div className={textMode ? 'fed-document-text' : 'sr-only'}>{textError ? (kz ? 'Мәтінді оқу мүмкін болмады. Құжатты көру үшін PDF режиміне ауысыңыз.' : 'Не удалось прочитать текст. Переключитесь в режим PDF для просмотра документа.') : text}</div></> : <img key={attempt} className="fed-document-image" src={url} alt={title} onLoad={() => setBusy(false)} onError={() => { setError(true); setBusy(false); }} />}
        </div>
        <footer className="fed-document-footer" aria-live="polite">{isPdf && pdf ? (kz ? `${page} / ${pdf.numPages} бет` : `Страница ${page} из ${pdf.numPages}`) : ''}<span>{kz ? 'Оқу үшін жүктеу қажет емес' : 'Для чтения скачивать не нужно'}</span></footer>
      </section>
    </div>, document.body,
  );
}
